import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PrestadorProfileView from '../components/PrestadorProfileView';
import { formatEta } from '../lib/date';
import {
  Bell, LogOut, MessageSquare, Send, Paperclip, Clock, MapPin,
  CheckCircle, XCircle, ChevronLeft, Loader2, Car, Gauge, Image, Flag, Mic, Square, Volume2, List, User,
  BellRing, Download, Smartphone, X
} from 'lucide-react';
import { enablePush, disablePush, getPushStatus, registerServiceWorker, pushSupported } from '../lib/push';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_API = API.replace('https://', 'wss://').replace('http://', 'ws://');

const computeEtaFromMinutes = (minutes) => {
  const target = new Date(Date.now() + minutes * 60000);
  const pad = n => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
};

const nowHHMM = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const combineWithToday = (timeStr) => {
  if (!timeStr) return '';
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${timeStr}`;
};

const extractHHMM = (dtStr) => {
  if (!dtStr) return '';
  const match = String(dtStr).match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : '';
};

const ETA_QUICK_OPTIONS = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1h' },
  { minutes: 120, label: '2h' },
];

// ─── Audio Recorder Hook ──────────────────────────────────────
const useAudioRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (e) {
      toast.error('Não foi possível acessar o microfone');
    }
  };

  const stop = () => {
    return new Promise((resolve) => {
      if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') { resolve(null); return; }
      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        mediaRecorder.current.stream.getTracks().forEach(t => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
        setDuration(0);
        resolve(blob);
      };
      mediaRecorder.current.stop();
    });
  };

  const cancel = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stream.getTracks().forEach(t => t.stop());
      mediaRecorder.current.stop();
    }
    clearInterval(timerRef.current);
    chunks.current = [];
    setRecording(false);
    setDuration(0);
  };

  return { recording, duration, start, stop, cancel };
};

// ─── Notification Sound ───────────────────────────────────────
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
    setTimeout(() => { const o2 = ctx.createOscillator(); o2.connect(gain); o2.frequency.value = 1100; o2.start(); o2.stop(ctx.currentTime + 0.15); }, 200);
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
  } catch (e) {}
};

const PrestadorPortal = () => {
  const { token, user, logout } = useAuth();
  const [view, setView] = useState('list');
  const [missions, setMissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedMission, setSelectedMission] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [chatView, setChatView] = useState('chat'); // chat | steps
  const [statusFilter, setStatusFilter] = useState('active');
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevNotifCount = useRef(0);
  const wsRef = useRef(null);
  const audio = useAudioRecorder();

  // Step form states
  const [stepEta, setStepEta] = useState('');
  const [stepKmStart, setStepKmStart] = useState('');
  const [stepArrival, setStepArrival] = useState('');
  const [stepKmEnd, setStepKmEnd] = useState('');
  const [stepFinishDt, setStepFinishDt] = useState('');
  const [stepNotes, setStepNotes] = useState('');
  const [stepFile, setStepFile] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [submittingStep, setSubmittingStep] = useState(false);
  const [availabilityEta, setAvailabilityEta] = useState('');
  const [customEtaMinutes, setCustomEtaMinutes] = useState('');
  const [evidenceList, setEvidenceList] = useState([]);
  const [pushStatus, setPushStatus] = useState('default');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem('pr_pwa_banner_dismissed') === '1');

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchMissions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/prestador/missions`, { headers });
      const data = await res.json();
      if (res.ok) setMissions(data.missions || []);
    } catch (e) {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/prestador/notifications`, { headers });
      const data = await res.json();
      if (res.ok) {
        const newNotifs = data.notifications || [];
        if (newNotifs.length > prevNotifCount.current && prevNotifCount.current > 0) {
          playNotificationSound();
          toast.info('Nova missão recebida!', { duration: 4000 });
        }
        prevNotifCount.current = newNotifs.length;
        setNotifications(newNotifs);
      }
    } catch (e) {}
  }, [token]);

  const fetchChat = useCallback(async (missionId) => {
    try {
      const res = await fetch(`${API}/api/prestador/mission/${missionId}/chat`, { headers });
      const data = await res.json();
      if (res.ok) setMessages(data.messages || []);
    } catch (e) {}
  }, [token]);

  const fetchEvidence = useCallback(async (missionId) => {
    try {
      const res = await fetch(`${API}/api/prestador/mission/${missionId}/evidence`, { headers });
      const data = await res.json();
      if (res.ok) setEvidenceList(data.evidence || []);
    } catch (e) {}
  }, [token]);

  const selectedMissionRef = useRef(null);
  useEffect(() => { selectedMissionRef.current = selectedMission; }, [selectedMission]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchMissions(), fetchNotifications()]);
      setLoading(false);
    };
    load();
    const interval = setInterval(() => {
      fetchMissions();
      fetchNotifications();
      if (selectedMissionRef.current) fetchChat(selectedMissionRef.current.id);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // PWA: register service worker, track push status + install prompt
  useEffect(() => {
    let mounted = true;
    (async () => {
      await registerServiceWorker();
      const status = await getPushStatus();
      if (mounted) setPushStatus(status);
    })();

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => {
      mounted = false;
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const handleEnablePush = async () => {
    try {
      await enablePush(token);
      setPushStatus('subscribed');
      toast.success('Notificações ativadas! Você será avisado de novas missões.');
    } catch (e) {
      toast.error(e.message || 'Não foi possível ativar as notificações');
      const st = await getPushStatus();
      setPushStatus(st);
    }
  };

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App instalado! Procure o ícone na tela inicial.');
        setInstallPrompt(null);
      }
    } else {
      // iOS / navegadores sem beforeinstallprompt: mostra instruções manuais
      setShowIosHint(true);
    }
  };

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('pr_pwa_banner_dismissed', '1');
  };

  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
  );

  const openChat = async (mission) => {
    setSelectedMission(mission);
    setView('chat');
    setShowSteps(true);
    await fetchChat(mission.id);
    try {
      const detRes = await fetch(`${API}/api/prestador/mission/${mission.id}`, { headers });
      if (detRes.ok) setSelectedMission(await detRes.json());
    } catch (e) {}
  };

  // Real-time chat sync via WebSocket (auto-reconnects with backoff; 4s polling above is the safety net)
  useEffect(() => {
    if (view !== 'chat' || !selectedMission?.id) {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      return;
    }
    const missionId = selectedMission.id;
    let closedIntentionally = false;
    let reconnectDelay = 1000;
    let reconnectTimer = null;
    let heartbeat = null;

    const connect = () => {
      const ws = new WebSocket(`${WS_API}/api/ws/mission/${missionId}/chat?token=${token}`);
      ws.onopen = () => {
        reconnectDelay = 1000;
        heartbeat = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
      };
      ws.onmessage = () => {
        fetchChat(missionId);
        fetchMissions();
      };
      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat);
        if (!closedIntentionally) {
          reconnectTimer = setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, 10000);
        }
      };
      ws.onerror = () => { ws.close(); };
      wsRef.current = ws;
    };
    connect();

    return () => {
      closedIntentionally = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeat) clearInterval(heartbeat);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedMission?.id, token]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedMission) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      await fetch(`${API}/api/prestador/mission/${selectedMission.id}/chat`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      setNewMessage('');
      await fetchChat(selectedMission.id);
    } catch (e) { toast.error('Erro ao enviar'); }
    setSending(false);
  };

  const sendFile = async (file) => {
    if (!file || !selectedMission) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', file.name);
      formData.append('file', file);
      await fetch(`${API}/api/prestador/mission/${selectedMission.id}/chat`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      await fetchChat(selectedMission.id);
      toast.success('Arquivo enviado');
    } catch (e) { toast.error('Erro ao enviar arquivo'); }
    setSending(false);
  };

  const sendAudio = async () => {
    const blob = await audio.stop();
    if (!blob || !selectedMission) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', 'Áudio');
      formData.append('file', blob, `audio_${Date.now()}.webm`);
      await fetch(`${API}/api/prestador/mission/${selectedMission.id}/chat`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      await fetchChat(selectedMission.id);
      toast.success('Áudio enviado');
    } catch (e) { toast.error('Erro ao enviar áudio'); }
    setSending(false);
  };

  const respondAvailability = async (missionId, available) => {
    if (available && !availabilityEta) {
      toast.error('Informe a previsão de chegada');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('available', available);
      if (available && availabilityEta) formData.append('eta_text', availabilityEta);
      const res = await fetch(`${API}/api/prestador/mission/${missionId}/availability`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        toast.success(available ? 'Disponibilidade confirmada!' : 'Indisponibilidade informada');
        setAvailabilityEta('');
        await fetchMissions();
        await fetchNotifications();
        if (selectedMission?.id === missionId) {
          await fetchChat(missionId);
          const detRes = await fetch(`${API}/api/prestador/mission/${missionId}`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (detRes.ok) setSelectedMission(await detRes.json());
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao responder');
        await fetchMissions();
      }
    } catch (e) { toast.error('Erro'); }
  };

  const submitStep = async (stepName) => {
    setSubmittingStep(true);
    try {
      const formData = new FormData();
      let url = `${API}/api/prestador/mission/${selectedMission.id}/${stepName}`;

      // Get current geolocation for photo watermark
      let geoLat = null, geoLng = null;
      try {
        const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
        geoLat = pos.coords.latitude;
        geoLng = pos.coords.longitude;
      } catch (e) { /* location unavailable */ }

      if (stepName === 'eta') {
        if (!stepEta) { toast.error('Informe a previsão'); setSubmittingStep(false); return; }
        formData.append('eta_datetime', stepEta);
      } else if (stepName === 'km-start') {
        if (!stepKmStart) { toast.error('Informe o KM'); setSubmittingStep(false); return; }
        formData.append('km_value', stepKmStart);
        if (!stepFile) { toast.error('Foto do painel obrigatória'); setSubmittingStep(false); return; }
        formData.append('photo', stepFile);
        if (geoLat) formData.append('lat', geoLat);
        if (geoLng) formData.append('lng', geoLng);
      } else if (stepName === 'arrival') {
        if (!stepArrival) { toast.error('Informe o horário de chegada'); setSubmittingStep(false); return; }
        formData.append('arrival_datetime', combineWithToday(stepArrival));
      } else if (stepName === 'evidence') {
        if (!stepFile) { toast.error('Arquivo obrigatório'); setSubmittingStep(false); return; }
        formData.append('file', stepFile);
        formData.append('description', stepNotes || 'Evidência');
        if (geoLat) formData.append('lat', geoLat);
        if (geoLng) formData.append('lng', geoLng);
      } else if (stepName === 'km-end') {
        if (!stepKmEnd) { toast.error('Informe o KM'); setSubmittingStep(false); return; }
        formData.append('km_value', stepKmEnd);
        if (!stepFile) { toast.error('Foto do painel obrigatória'); setSubmittingStep(false); return; }
        formData.append('photo', stepFile);
        if (geoLat) formData.append('lat', geoLat);
        if (geoLng) formData.append('lng', geoLng);
      } else if (stepName === 'finalize') {
        if (!stepFinishDt) { toast.error('Informe o horário de finalização'); setSubmittingStep(false); return; }
        formData.append('finish_datetime', combineWithToday(stepFinishDt));
        formData.append('notes', stepNotes);
      }

      const res = await fetch(url, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        if (stepName === 'evidence') {
          toast.success('Evidência enviada! Envie mais ou clique em "Concluir Evidências"');
          setStepFile(null);
          setStepNotes('');
          await fetchEvidence(selectedMission.id);
          // Don't close the step form - keep it open for more uploads
        } else {
          toast.success('Etapa concluída!');
          setActiveStep(null);
          setStepFile(null);
          setStepNotes('');
          setStepEta(''); setStepKmStart(''); setStepArrival(''); setStepKmEnd(''); setStepFinishDt('');
        }
        await fetchChat(selectedMission.id);
        await fetchMissions();
        const detRes = await fetch(`${API}/api/prestador/mission/${selectedMission.id}`, { headers });
        if (detRes.ok) setSelectedMission(await detRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (e) { toast.error('Erro ao enviar etapa'); }
    setSubmittingStep(false);
  };

  const markEvidenceDone = async () => {
    setSubmittingStep(true);
    try {
      const res = await fetch(`${API}/api/prestador/mission/${selectedMission.id}/evidence-done`, {
        method: 'POST', headers
      });
      if (res.ok) {
        toast.success('Evidências concluídas!');
        setActiveStep(null);
        await fetchChat(selectedMission.id);
        await fetchMissions();
        const detRes = await fetch(`${API}/api/prestador/mission/${selectedMission.id}`, { headers });
        if (detRes.ok) setSelectedMission(await detRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Envie ao menos 1 foto para concluir');
      }
    } catch (e) { toast.error('Erro'); }
    setSubmittingStep(false);
  };

  const getStatusBadge = (mission) => {
    const map = {
      criada: { label: 'Nova', cls: 'bg-blue-100 text-blue-700' },
      aguardando_inicio: { label: 'Aguardando', cls: 'bg-amber-100 text-amber-700' },
      em_andamento: { label: 'Em Andamento', cls: 'bg-purple-100 text-purple-700' },
      finalizada: { label: 'Finalizada', cls: 'bg-green-100 text-green-700' },
      aguardando_validacao: { label: 'Ag. Validação', cls: 'bg-indigo-100 text-indigo-700' },
      cancelada: { label: 'Cancelada', cls: 'bg-red-100 text-red-700' },
    };
    // Prestadores NÃO selecionados não devem acompanhar o status de execução (em andamento,
    // finalizada, etc.) de uma missão que está sendo atendida por outro prestador.
    if (!mission.is_assigned_to_me) {
      if (mission.status === 'cancelada') {
        return <Badge className="bg-red-100 text-red-700 text-xs">Cancelada</Badge>;
      }
      if (mission.locked_for_others) {
        return <Badge className="bg-slate-100 text-slate-500 text-xs">Não selecionada</Badge>;
      }
      if (mission.my_availability?.available) {
        return <Badge className="bg-amber-100 text-amber-700 text-xs">Disponibilidade enviada</Badge>;
      }
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Nova</Badge>;
    }
    const s = map[mission.status] || { label: mission.status, cls: 'bg-slate-100 text-slate-700' };
    return <Badge className={`${s.cls} text-xs`}>{s.label}</Badge>;
  };

  const getCurrentStepIndex = (mission) => {
    const steps = mission?.steps || {};
    if (steps.finalized_at) return 6;
    if (steps.km_end) return 5;
    if (steps.evidence_done) return 4;
    if (steps.arrival_datetime) return 3;
    if (steps.km_start) return 2;
    if (mission.my_availability?.available) return 1;
    return 0;
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };


  // ═══════════════════════════════════════════════════════════════
  // RENDER: Mission List
  // ═══════════════════════════════════════════════════════════════
  const renderList = () => (
    <div className="relative flex flex-col h-[100dvh] bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 pt-[env(safe-area-inset-top,12px)] pb-3 flex items-center justify-between">
        <div className="pt-2">
          <h1 className="text-lg font-bold tracking-tight">Pronta Resposta</h1>
          <p className="text-xs text-slate-400">{user?.supplier_name || user?.name}</p>
        </div>
        <div className="flex items-center gap-4 pt-2">
          <button onClick={fetchNotifications} className="relative p-1" data-testid="notifications-btn">
            <Bell className="w-5 h-5" />
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">
                {notifications.length}
              </span>
            )}
          </button>
          <button onClick={logout} className="p-1" data-testid="prestador-logout-btn"><LogOut className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Notifications - Slide in animation */}
      {notifications.length > 0 && (
        <div className="px-3 pt-3 space-y-2">
          {notifications.slice(0, 3).map((n, idx) => (
            <div key={n.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 animate-in slide-in-from-top"
              style={{ animationDelay: `${idx * 100}ms` }}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-900 leading-tight">{n.title}</p>
                  <p className="text-xs text-amber-700 mt-0.5">{n.body}</p>
                  {n.mission_data?.origin_address && (
                    <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" /> {n.mission_data.origin_address}
                    </p>
                  )}
                  {n.mission_data?.google_maps_link && (
                    <a href={n.mission_data.google_maps_link} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 font-medium hover:underline mt-1 inline-flex items-center gap-1"
                      onClick={e => e.stopPropagation()}>
                      <MapPin className="w-3 h-3" /> Abrir no Google Maps
                    </a>
                  )}
                  {(n.mission_data?.origin_lat && !n.mission_data?.google_maps_link) && (
                    <a href={`https://www.google.com/maps?q=${n.mission_data.origin_lat},${n.mission_data.origin_lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 font-medium hover:underline mt-1 inline-flex items-center gap-1"
                      onClick={e => e.stopPropagation()}>
                      <MapPin className="w-3 h-3" /> Abrir no Google Maps
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button size="sm" className="h-7 px-3 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg shadow-sm"
                    onClick={() => {
                      const mission = missions.find(m => m.id === n.mission_id);
                      if (mission) openChat(mission);
                      else { respondAvailability(n.mission_id, true); }
                    }} data-testid={`accept-${n.mission_id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Ver
                  </Button>
                  <Button size="sm" className="h-7 px-3 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs rounded-lg"
                    onClick={() => respondAvailability(n.mission_id, false)} data-testid={`reject-${n.mission_id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Não
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PWA: Install + Enable notifications banner */}
      {!bannerDismissed && !(isStandalone && pushStatus === 'subscribed') && (
        <div className="px-3 pt-3" data-testid="pwa-banner">
          <div className="relative bg-slate-900 text-white rounded-2xl p-4 shadow-lg overflow-hidden">
            <button onClick={dismissBanner} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-white" data-testid="pwa-banner-dismiss">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <BellRing className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-bold leading-tight">Receba novas missões no celular</p>
                <p className="text-xs text-slate-400 mt-0.5">Instale o app e ative as notificações para ser avisado mesmo com o app fechado.</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {!isStandalone && (
                    <Button size="sm" onClick={handleInstall}
                      className="h-8 px-3 bg-white text-slate-900 hover:bg-slate-100 text-xs rounded-lg font-semibold"
                      data-testid="pwa-install-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> Instalar app
                    </Button>
                  )}
                  {pushSupported() && pushStatus !== 'subscribed' && (
                    <Button size="sm" onClick={handleEnablePush}
                      className="h-8 px-3 bg-amber-500 text-white hover:bg-amber-600 text-xs rounded-lg font-semibold"
                      data-testid="pwa-enable-push-btn">
                      <Bell className="w-3.5 h-3.5 mr-1" /> Ativar notificações
                    </Button>
                  )}
                  {pushStatus === 'subscribed' && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400 font-medium" data-testid="pwa-push-active">
                      <CheckCircle className="w-3.5 h-3.5" /> Notificações ativas
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iOS install hint */}
      {showIosHint && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosHint(false)} data-testid="ios-install-hint">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-5 h-5 text-slate-900" />
              <h3 className="text-base font-bold text-slate-900">Instalar no iPhone / iPad</h3>
            </div>
            <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
              <li>Toque no botão <strong>Compartilhar</strong> (ícone de caixa com seta) no Safari.</li>
              <li>Selecione <strong>"Adicionar à Tela de Início"</strong>.</li>
              <li>Abra o app pela tela inicial e toque em <strong>"Ativar notificações"</strong>.</li>
            </ol>
            <Button className="w-full mt-4 bg-slate-900 text-white" onClick={() => setShowIosHint(false)} data-testid="ios-hint-close">
              Entendi
            </Button>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <div className="px-3 pt-3">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { key: 'active', label: 'Ativas', count: missions.filter(m => ['criada','aguardando_inicio','em_andamento'].includes(m.status)).length },
            { key: 'done', label: 'Concluídas', count: missions.filter(m => ['finalizada','aguardando_validacao','aprovado_faturamento','faturado'].includes(m.status)).length },
            { key: 'cancelled', label: 'Canceladas', count: missions.filter(m => m.status === 'cancelada').length },
            { key: 'all', label: 'Todas', count: missions.length }
          ].map(tab => (
            <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border transition-all duration-200 flex items-center gap-1.5 ${
                statusFilter === tab.key
                  ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}>
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === tab.key ? 'bg-white/20' : 'bg-slate-100'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Mission Cards */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-2 pb-24 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : missions.filter(m => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return ['criada', 'aguardando_inicio', 'em_andamento'].includes(m.status);
            if (statusFilter === 'done') return ['finalizada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'].includes(m.status);
            if (statusFilter === 'cancelled') return m.status === 'cancelada';
            return true;
          }).length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nenhuma missão encontrada</p>
          </div>
        ) : (
          missions.filter(m => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return ['criada', 'aguardando_inicio', 'em_andamento'].includes(m.status);
            if (statusFilter === 'done') return ['finalizada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'].includes(m.status);
            if (statusFilter === 'cancelled') return m.status === 'cancelada';
            return true;
          }).map((m, idx) => {
            const stepIdx = getCurrentStepIndex(m);
            const totalSteps = 7;
            const progress = m.is_assigned_to_me ? Math.round((stepIdx / totalSteps) * 100) : 0;
            return (
              <Card key={m.id}
                className="border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.98]"
                onClick={() => openChat(m)} data-testid={`mission-card-${m.id}`}
                style={{ animationDelay: `${idx * 50}ms` }}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                        <Car className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="font-bold text-sm text-slate-900">{m.vehicle_plate || 'N/A'}</span>
                        <p className="text-[10px] text-slate-400">{m.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.unread_messages > 0 && (
                        <span className="w-5 h-5 bg-amber-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">
                          {m.unread_messages}
                        </span>
                      )}
                      {getStatusBadge(m)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-1.5">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(m.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {m.origin_address && (
                    <div className="flex items-start gap-1 text-[11px] text-slate-500 mb-2">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="truncate">{m.origin_address}</span>
                    </div>
                  )}
                  {/* Progress bar */}
                  {m.is_assigned_to_me && (
                    <div className="mt-1">
                      <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                        <span>Progresso</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <BottomNav view={view} setView={setView} />
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // RENDER: Profile
  // ═══════════════════════════════════════════════════════════════
  const renderProfile = () => (
    <div className="relative flex flex-col h-[100dvh] bg-slate-50 overflow-hidden">
      <div className="bg-slate-900 text-white px-4 pt-[env(safe-area-inset-top,12px)] pb-3 flex items-center justify-between">
        <div className="pt-2">
          <h1 className="text-lg font-bold tracking-tight">Meu Perfil</h1>
          <p className="text-xs text-slate-400">{user?.name}</p>
        </div>
        <button onClick={logout} className="p-1 pt-2" data-testid="prestador-logout-btn-profile"><LogOut className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-3 pb-24">
        <PrestadorProfileView mode="own" />
      </div>
      <BottomNav view={view} setView={setView} />
    </div>
  );
  // RENDER: Chat View
  // ═══════════════════════════════════════════════════════════════
  const renderChat = () => {
    if (!selectedMission) return null;
    const m = selectedMission;
    const stepIdx = getCurrentStepIndex(m);
    const isAssigned = m.is_assigned_to_me;
    // Full mission details (plate, driver, phone, customer) only unlock once the gestor
    // has actually SELECTED this prestador - declaring availability alone is not enough
    const hasAccepted = isAssigned;

    const stepsConfig = [
      { key: 'availability', label: 'Disponibilidade', icon: CheckCircle, color: 'green' },
      { key: 'km-start', label: 'KM Inicial + Foto', icon: Gauge, color: 'indigo' },
      { key: 'arrival', label: 'Chegada no Local', icon: MapPin, color: 'purple' },
      { key: 'evidence', label: 'Evidências', icon: Image, color: 'pink' },
      { key: 'km-end', label: 'KM Final + Foto', icon: Gauge, color: 'orange' },
      { key: 'finalize', label: 'Finalizar Missão', icon: Flag, color: 'emerald' },
    ];

    // Prestador pode navegar livremente entre as etapas; só não pode reabrir
    // Disponibilidade (ETA já travado) nem Finalizar sem preencher as demais
    const allStepsReady = !!(m.steps?.km_start && m.steps?.arrival_datetime && m.steps?.evidence_done && m.steps?.km_end);
    const doneMap = {
      availability: !!m.my_availability,
      'km-start': !!m.steps?.km_start,
      arrival: !!m.steps?.arrival_datetime,
      evidence: !!m.steps?.evidence_done,
      'km-end': !!m.steps?.km_end,
      finalize: !!m.steps?.finalized_at,
    };

    return (
      <div className="flex flex-col h-[100dvh] bg-slate-50 overflow-hidden">
        {/* Chat Header */}
        <div className="bg-slate-900 text-white px-3 pt-[env(safe-area-inset-top,8px)] pb-3 flex items-center gap-3">
          <button onClick={() => { setView('list'); setSelectedMission(null); }} className="pt-1" data-testid="back-to-list">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm">{m.vehicle_plate || m.code}</span>
              {getStatusBadge(m)}
            </div>
            <p className="text-[11px] text-slate-400 truncate">{m.origin_address || m.code}</p>
          </div>
        </div>

        {/* Selection status banner */}
        {isAssigned && m.selection_locked && (
          <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center gap-2" data-testid="mission-selected-notice">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-green-700">Você foi selecionado para esta missão! 🎉</p>
          </div>
        )}

        {/* Tab bar: Chat / Etapas */}
        {isAssigned && (
          <div className="bg-white border-b flex">
            <button onClick={() => setChatView('chat')}
              className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${chatView === 'chat' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-400'}`}
              data-testid="prestador-tab-chat">
              <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Mensagens
            </button>
            <button onClick={() => setChatView('steps')}
              className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${chatView === 'steps' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-400'}`}
              data-testid="prestador-tab-steps">
              <Flag className="w-3.5 h-3.5 inline mr-1" /> Etapas {stepIdx}/{stepsConfig.length}
            </button>
          </div>
        )}

        {/* Steps View */}
        {chatView === 'steps' && isAssigned && (
          <div className="flex-1 min-h-0 overflow-y-auto bg-white px-3 py-3">
            {/* Progress summary */}
            <div className="flex items-center gap-3 mb-3 px-1">
              <div className="flex-1">
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-green-500 rounded-full transition-all duration-700"
                    style={{ width: `${Math.round((stepIdx / stepsConfig.length) * 100)}%` }} />
                </div>
              </div>
              <span className="text-xs font-medium text-slate-500">{stepIdx}/{stepsConfig.length}</span>
            </div>

            <div className="space-y-1.5">
              {stepsConfig.map((step, idx) => {
                const Icon = step.icon;
                const done = doneMap[step.key];
                const clickable = step.key !== 'availability' && !(step.key === 'finalize' && !allStepsReady);
                const isOpen = activeStep === step.key;
                return (
                  <div key={step.key}>
                    <button
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 ${
                        done ? 'bg-green-50' : clickable ? 'bg-white border border-slate-200 shadow-sm hover:border-amber-300' : 'bg-slate-50 opacity-40'
                      }`}
                      onClick={() => {
                        if (!clickable) return;
                        if (!isOpen) {
                          if (step.key === 'km-start') setStepKmStart(m.steps?.km_start != null ? String(m.steps.km_start) : '');
                          if (step.key === 'arrival') setStepArrival(extractHHMM(m.steps?.arrival_datetime) || '');
                          if (step.key === 'km-end') setStepKmEnd(m.steps?.km_end != null ? String(m.steps.km_end) : '');
                          if (step.key === 'evidence') fetchEvidence(m.id);
                          setStepFile(null);
                        }
                        setActiveStep(isOpen ? null : step.key);
                      }}
                      disabled={!clickable}
                      data-testid={`step-${step.key}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        done ? 'bg-green-500 text-white scale-90' : clickable ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-400'
                      }`}>
                        {done ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span className={`text-sm flex-1 text-left ${done ? 'text-green-700' : clickable ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                        {step.label}
                      </span>
                      {step.key === 'availability' && m.my_availability?.eta_text && (
                        <span className="text-xs text-slate-400 font-mono">{formatEta(m.my_availability.eta_text)}</span>
                      )}
                      {!done && clickable && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>}
                      {done && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </button>

                    {/* Inline Step Form */}
                    {isOpen && (
                      <div className="mt-1.5 ml-11 mr-1 p-3 bg-white border border-amber-100 rounded-xl space-y-3 animate-in slide-in-from-top duration-200">
                        {activeStep === 'km-start' && (
                          <>
                            <div>
                              <label className="text-xs font-medium text-slate-600">KM Inicial</label>
                              <Input type="number" value={stepKmStart} onChange={e => setStepKmStart(e.target.value)} placeholder="Ex: 145230" className="mt-1 h-10" data-testid="step-km-start-input" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600">Foto do Painel *</label>
                              <Input type="file" accept="image/*" capture="environment" onChange={e => setStepFile(e.target.files[0])} className="mt-1" data-testid="step-km-start-photo" />
                            </div>
                          </>
                        )}
                        {activeStep === 'arrival' && (
                          <div>
                            <label className="text-xs font-medium text-slate-600">Horário de Chegada</label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input type="time" value={stepArrival} onChange={e => setStepArrival(e.target.value)} className="h-10" data-testid="step-arrival-input" />
                              <Button type="button" size="sm" variant="outline" className="h-10 whitespace-nowrap text-xs" onClick={() => setStepArrival(nowHHMM())} data-testid="step-arrival-now-btn">
                                Agora
                              </Button>
                            </div>
                          </div>
                        )}
                        {activeStep === 'evidence' && (
                          <>
                            {evidenceList.length > 0 && (
                              <div>
                                <label className="text-xs font-medium text-slate-600">Evidências enviadas ({evidenceList.length})</label>
                                <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="evidence-gallery">
                                  {evidenceList.map(ev => (
                                    <div key={ev.id} className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`evidence-item-${ev.id}`}>
                                      {ev.type === 'photo' ? (
                                        <img src={`${API}/api/files/${ev.storage_path}`} alt={ev.description || 'Evidência'} className="w-full h-16 object-cover" />
                                      ) : (
                                        <div className="w-full h-16 flex items-center justify-center bg-slate-100"><Image className="w-5 h-5 text-slate-400" /></div>
                                      )}
                                      {ev.description && <p className="text-[10px] text-slate-500 px-1 py-0.5 truncate">{ev.description}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div>
                              <label className="text-xs font-medium text-slate-600">Foto ou Vídeo *</label>
                              <Input type="file" accept="image/*,video/*" capture="environment" onChange={e => setStepFile(e.target.files[0])} className="mt-1" data-testid="step-evidence-file" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600">Descrição</label>
                              <Input value={stepNotes} onChange={e => setStepNotes(e.target.value)} placeholder="Descreva a evidência" className="mt-1 h-10" data-testid="step-evidence-desc" />
                            </div>
                          </>
                        )}
                        {activeStep === 'km-end' && (
                          <>
                            <div>
                              <label className="text-xs font-medium text-slate-600">KM Final</label>
                              <Input type="number" value={stepKmEnd} onChange={e => setStepKmEnd(e.target.value)} placeholder="Ex: 145280" className="mt-1 h-10" data-testid="step-km-end-input" />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600">Foto do Painel *</label>
                              <Input type="file" accept="image/*" capture="environment" onChange={e => setStepFile(e.target.files[0])} className="mt-1" data-testid="step-km-end-photo" />
                            </div>
                          </>
                        )}
                        {activeStep === 'finalize' && (
                          <>
                            <div>
                              <label className="text-xs font-medium text-slate-600">Horário de Finalização</label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input type="time" value={stepFinishDt} onChange={e => setStepFinishDt(e.target.value)} className="h-10" data-testid="step-finalize-dt" />
                                <Button type="button" size="sm" variant="outline" className="h-10 whitespace-nowrap text-xs" onClick={() => setStepFinishDt(nowHHMM())} data-testid="step-finalize-now-btn">
                                  Agora
                                </Button>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600">Observações</label>
                              <Input value={stepNotes} onChange={e => setStepNotes(e.target.value)} placeholder="Observações finais" className="mt-1 h-10" data-testid="step-finalize-notes" />
                            </div>
                          </>
                        )}
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="outline" onClick={() => setActiveStep(null)} className="flex-1 h-9 rounded-lg text-xs">Cancelar</Button>
                          {activeStep === 'evidence' ? (
                            <>
                              <Button size="sm" onClick={() => submitStep('evidence')} disabled={submittingStep || !stepFile}
                                className="flex-1 h-9 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs" data-testid="submit-step-btn">
                                {submittingStep ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Evidência'}
                              </Button>
                              <Button size="sm" onClick={markEvidenceDone} disabled={submittingStep || evidenceList.length === 0}
                                className="flex-1 h-9 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" data-testid="evidence-done-btn">
                                Concluir
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" onClick={() => submitStep(activeStep)} disabled={submittingStep}
                              className="flex-1 h-9 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs" data-testid="submit-step-btn">
                              {submittingStep ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Chat View */}
        {(chatView === 'chat' || !isAssigned) && (
          <>
        {/* Locked notice: another prestador was already selected */}
        {!isAssigned && m.locked_for_others && (
          <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center gap-2" data-testid="mission-locked-notice">
            <XCircle className="w-4 h-4 text-slate-400" />
            <p className="text-sm text-slate-500">Você não foi selecionado para esta missão. Ela foi atribuída a outro prestador.</p>
          </div>
        )}
        {/* Availability prompt */}
        {!isAssigned && !m.locked_for_others && !m.my_availability && m.status === 'criada' && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-4 py-4">
            <p className="text-sm font-semibold text-amber-900 mb-2">Você está disponível para esta missão?</p>
            {(m.origin_address || m.location_address) && (
              <p className="text-xs text-amber-700 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {m.origin_address || m.location_address}
              </p>
            )}
            {((m.origin_lat || m.location_lat) && (m.origin_lng || m.location_lng)) && (
              <a href={`https://www.google.com/maps?q=${m.origin_lat || m.location_lat},${m.origin_lng || m.location_lng}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 font-medium hover:underline mb-3 inline-flex items-center gap-1"
                onClick={e => e.stopPropagation()}>
                <MapPin className="w-3 h-3" /> Ver localização no Google Maps
              </a>
            )}
            <div className="mb-3 mt-2">
              <label className="text-xs font-medium text-amber-800">Previsão de chegada no local *</label>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {ETA_QUICK_OPTIONS.map(opt => (
                  <button key={opt.minutes} type="button" onClick={() => setAvailabilityEta(computeEtaFromMinutes(opt.minutes))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      availabilityEta === computeEtaFromMinutes(opt.minutes) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-100'
                    }`}
                    data-testid={`eta-quick-${opt.minutes}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" min="1" value={customEtaMinutes} onChange={e => setCustomEtaMinutes(e.target.value)}
                  placeholder="Outro (minutos)" className="h-9 bg-white text-sm" data-testid="availability-eta-custom-input" />
                <Button type="button" size="sm" variant="outline" className="h-9 whitespace-nowrap"
                  onClick={() => { if (customEtaMinutes) setAvailabilityEta(computeEtaFromMinutes(parseInt(customEtaMinutes))); }}
                  data-testid="availability-eta-custom-apply">
                  Aplicar
                </Button>
              </div>
              {availabilityEta && (
                <p className="text-xs text-amber-700 mt-1.5">Chegada prevista: <span className="font-semibold">{formatEta(availabilityEta)}</span></p>
              )}
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white rounded-xl shadow-sm"
                onClick={() => respondAvailability(m.id, true)}>
                <CheckCircle className="w-4 h-4 mr-2" /> Disponível
              </Button>
              <Button className="flex-1 h-11 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl"
                onClick={() => respondAvailability(m.id, false)}>
                <XCircle className="w-4 h-4 mr-2" /> Indisponível
              </Button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2" data-testid="chat-messages-container">
          {/* Mission info card - progressive visibility: only location + reason before acceptance */}
          <div className="bg-white rounded-xl p-3 border shadow-sm mb-3" data-testid="mission-info-card">
            {hasAccepted ? (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Placa:</span> <span className="font-semibold text-slate-800">{m.vehicle_plate}</span></div>
                <div><span className="text-slate-400">Motorista:</span> <span className="font-semibold text-slate-800">{m.driver_name || 'N/A'}</span></div>
                <div><span className="text-slate-400">Tel:</span> <span className="font-semibold text-slate-800">{m.driver_phone || 'N/A'}</span></div>
                <div><span className="text-slate-400">Tipo:</span> <span className="font-semibold text-slate-800">{m.vehicle_type || 'N/A'}</span></div>
                {m.customer_name && <div className="col-span-2"><span className="text-slate-400">Cliente:</span> <span className="font-semibold text-slate-800">{m.customer_name}</span></div>}
                {m.reason_name && <div className="col-span-2"><span className="text-slate-400">Motivo:</span> <span className="font-semibold text-slate-800">{m.reason_name}</span></div>}
                {(m.origin_address || m.location_address) && (
                  <div className="col-span-2">
                    <span className="text-slate-400">Local:</span> <span className="font-semibold text-slate-800">{m.origin_address || m.location_address}</span>
                  </div>
                )}
                {((m.origin_lat || m.location_lat) && (m.origin_lng || m.location_lng)) && (
                  <div className="col-span-2">
                    <a href={`https://www.google.com/maps?q=${m.origin_lat || m.location_lat},${m.origin_lng || m.location_lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 font-medium hover:underline"
                      data-testid="prestador-maps-link">
                      <MapPin className="w-3 h-3" /> Abrir no Google Maps
                    </a>
                  </div>
                )}
                {(m.observations || m.description) && <div className="col-span-2"><span className="text-slate-400">Obs:</span> <span className="font-medium text-slate-700">{m.observations || m.description}</span></div>}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 text-xs" data-testid="mission-info-card-minimal">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Dados do cliente protegidos até aceite</p>
                {(m.origin_address || m.location_address) && (
                  <div><span className="text-slate-400">Local:</span> <span className="font-semibold text-slate-800">{m.origin_address || m.location_address}</span></div>
                )}
                {m.reason_name && <div><span className="text-slate-400">Motivo:</span> <span className="font-semibold text-slate-800">{m.reason_name}</span></div>}
              </div>
            )}
          </div>

          {messages.map((msg, idx) => (
            <div key={msg.id}
              className={`flex ${msg.sender_type === 'prestador' ? 'justify-end' : 'justify-start'}`}
              style={{ animationDelay: `${idx * 30}ms` }}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 shadow-sm ${
                msg.sender_type === 'prestador'
                  ? 'bg-amber-500 text-white rounded-br-sm'
                  : msg.sender_type === 'system'
                  ? 'bg-slate-100 text-slate-600 rounded-bl-sm text-center w-full max-w-full text-xs italic'
                  : 'bg-white text-slate-900 border border-slate-100 rounded-bl-sm'
              }`}>
                {msg.sender_type !== 'prestador' && msg.sender_type !== 'system' && (
                  <p className="text-[10px] font-bold text-amber-600 mb-0.5">{msg.sender_name}</p>
                )}
                {msg.has_media && msg.media_type?.startsWith('audio') && (
                  <div className="flex items-center gap-2 mb-1">
                    <Volume2 className="w-4 h-4" />
                    <span className="text-xs">Áudio</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                <p className={`text-[10px] mt-1 text-right ${msg.sender_type === 'prestador' ? 'text-amber-200' : 'text-slate-400'}`}>
                  {new Date(msg.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Chat Input */}
        {isAssigned && !['cancelada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'].includes(m.status) && (
          <div className="bg-white border-t px-3 py-2 pb-[env(safe-area-inset-bottom,8px)] flex items-center gap-2">
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*,video/*"
              onChange={e => { if (e.target.files[0]) sendFile(e.target.files[0]); }} />
            <button onClick={() => fileInputRef.current?.click()} className="text-slate-400 hover:text-slate-600 p-1.5">
              <Paperclip className="w-5 h-5" />
            </button>

            {audio.recording ? (
              <div className="flex-1 flex items-center gap-3 bg-red-50 rounded-full px-4 h-10 border border-red-200">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-mono text-red-600 flex-1">{formatDuration(audio.duration)}</span>
                <button onClick={audio.cancel} className="text-slate-400 hover:text-red-500 p-1">
                  <XCircle className="w-5 h-5" />
                </button>
                <button onClick={sendAudio} className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white" data-testid="send-audio-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Mensagem..."
                  className="flex-1 rounded-full border-slate-200 h-10 text-sm focus:ring-amber-400" data-testid="chat-input"
                  onKeyDown={e => e.key === 'Enter' && sendMessage()} />
                {newMessage.trim() ? (
                  <Button size="icon" onClick={sendMessage} disabled={sending}
                    className="w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-600 shadow-sm" data-testid="chat-send-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                ) : (
                  <button onClick={audio.start} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="record-audio-btn">
                    <Mic className="w-5 h-5 text-slate-600" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
          </>
        )}
      </div>
    );
  };

  return view === 'list' ? renderList() : view === 'profile' ? renderProfile() : renderChat();
};

const BottomNav = ({ view, setView }) => (
  <div className="absolute bottom-0 left-0 right-0 bg-white border-t flex pb-[env(safe-area-inset-bottom,0px)]" data-testid="prestador-bottom-nav">
    <button onClick={() => setView('list')}
      className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors ${view === 'list' ? 'text-amber-600' : 'text-slate-400'}`}
      data-testid="nav-missions-btn">
      <List className="w-5 h-5" />
      <span className="text-[10px] font-medium">Missões</span>
    </button>
    <button onClick={() => setView('profile')}
      className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors ${view === 'profile' ? 'text-amber-600' : 'text-slate-400'}`}
      data-testid="nav-profile-btn">
      <User className="w-5 h-5" />
      <span className="text-[10px] font-medium">Perfil</span>
    </button>
  </div>
);

export default PrestadorPortal;
